import React, { useEffect, useState } from 'react'
import { HelpCircle as HelpCircleIcon } from 'react-feather'
import { Box, Button, Tab, Tabs, Tooltip } from '@material-ui/core'

import {
  Container,
  ContentHeader,
  DriveTour,
  FilterButton,
  LoadingFeedback,
  Page,
  Permitted,
} from 'components'
import {
  DocumentCreationModeDialog,
  DocumentsFilters,
  DocumentTemplates,
  MyDocuments,
} from './components'

import helpers from 'helpers'

import useAuth from 'hooks/useAuth'
import useFetch from 'hooks/useFetch'
import useFilter from 'hooks/useFilter'
import usePermissions from 'hooks/usePermissions'

import theme from 'theme'
import * as services from 'service'
import constants from 'constants/index'

const DocumentsMain = () => {
  const {
    AWAITING_APPROVAL_STATUS_ID,
    CATEGORIES_CARD_TABS,
    DOCUMENTS_CATEGORY_ID,
    DOCUMENT_TEMPLATES_CATEGORY_ID,
    PENDING_APPROVAL_DOCUMENTS_CATEGORY_ID,
  } = constants.documents

  const {
    MAIN_STEPS_OBJECT,
    MAIN_STEP_1,
    MAIN_STEP_2,
    MAIN_STEP_3,
    MAIN_STEP_4,
    MAIN_STEP_6,
    MAIN_STEP_7,
  } = constants.tours.documents

  const { PRIVACY_POLICIES, DOCUMENTS_TEMPLATE } = constants.permissions

  const [loading, setLoading] = useState()
  const [openTour, setOpenTour] = useState(false)
  const [currentTab, setCurrentTab] = useState(DOCUMENTS_CATEGORY_ID)
  const [hasDocument, setHasDocument] = useState(false)
  const [renderFilter, setRenderFilter] = useState(true)

  const filter = useFilter()
  const permissions = usePermissions()
  const { company } = useAuth()

  const docCreatePermission = permissions.permitted(PRIVACY_POLICIES.CREATE)
  const docListPermission = permissions.permitted(PRIVACY_POLICIES.LIST)
  const modelListPermission = permissions.permitted(DOCUMENTS_TEMPLATE.LIST)

  const isTemplateTab = currentTab === DOCUMENT_TEMPLATES_CATEGORY_ID

  const { response, isLoading } = useFetch(
    services.dponet.documentCategories.get,
    {},
    [isTemplateTab],
    !!isTemplateTab,
  )

  const documentCategories = response?.data?.documentCategories

  const filteredSteps = MAIN_STEPS_OBJECT.filter((step) => {
    if (!docCreatePermission) return step.element !== '#' + MAIN_STEP_2
    if (!docListPermission) return step.element !== '#' + MAIN_STEP_3
    if (!modelListPermission) return step.element !== '#' + MAIN_STEP_4
    if (!docListPermission) return step.element !== '#' + MAIN_STEP_6
    if (!hasDocument) return step.element !== '#' + MAIN_STEP_7

    return true
  })

  const handleTabChange = (_, clickedTab) => setCurrentTab(clickedTab)

  const handleOpenTour = () => {
    const where = 'Documentos'
    const params = {
      action: 'tour-listagem-documentos',
    }

    helpers.mixpanel.track(where, 'Tour', params)
    setOpenTour(true)
  }

  const disabledTab = (value) =>
    constants.userCompanies.TRIAL_STATUSES.includes(company?.statusId) &&
    value === DOCUMENT_TEMPLATES_CATEGORY_ID

  const filteredTabs = CATEGORIES_CARD_TABS.filter((tab) => {
    if (tab.permission) return permissions.permitted(tab.permission)
    if (tab.somePermissions) return permissions.some(tab.somePermissions)

    return false
  })

  useEffect(() => {
    if (docListPermission) {
      setCurrentTab(DOCUMENTS_CATEGORY_ID)
    } else if (modelListPermission) {
      setCurrentTab(DOCUMENT_TEMPLATES_CATEGORY_ID)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setRenderFilter(false)
    const timer = setTimeout(() => {
      setRenderFilter(true)
    }, 100)

    return () => clearTimeout(timer)
  }, [currentTab])

  return (
    <Page title="Documentos">
      <LoadingFeedback open={loading} />
      <Container maxWidth={false}>
        <ContentHeader title="Documentos" gridGap={theme.spacing(2)}>
          <Tooltip title="Iniciar o tour guiado">
            <Button
              variant="contained"
              color="primary"
              startIcon={<HelpCircleIcon size={20} />}
              onClick={handleOpenTour}
              id={MAIN_STEP_1}
              fullWidth
            >
              Tutorial
            </Button>
          </Tooltip>
          <Permitted tag={PRIVACY_POLICIES.CREATE}>
            <DocumentCreationModeDialog id={MAIN_STEP_2} />
          </Permitted>
          <FilterButton setDrawerOpen={filter.setDrawerOpen} />
        </ContentHeader>
        {!!currentTab && (
          <Box display="flex" flexDirection="column">
            <Tabs
              scrollButtons="auto"
              value={currentTab}
              variant="scrollable"
              textColor="primary"
              indicatorColor="primary"
              onChange={handleTabChange}
            >
              {filteredTabs?.map((tab, index) => (
                <Tab
                  key={index}
                  id={tab.id}
                  label={tab.label}
                  value={tab.value}
                  disabled={disabledTab(tab.value)}
                />
              ))}
            </Tabs>
            {currentTab === DOCUMENTS_CATEGORY_ID && (
              <MyDocuments
                setLoading={setLoading}
                setHasDocument={setHasDocument}
                filter={filter}
              />
            )}
            {currentTab === DOCUMENT_TEMPLATES_CATEGORY_ID && (
              <DocumentTemplates filter={filter} />
            )}
            {currentTab === PENDING_APPROVAL_DOCUMENTS_CATEGORY_ID && (
              <MyDocuments
                setLoading={setLoading}
                setHasDocument={setHasDocument}
                filter={filter}
                defaultFilters={{ status: AWAITING_APPROVAL_STATUS_ID }}
              />
            )}
          </Box>
        )}
      </Container>
      {renderFilter && (
        <DocumentsFilters
          key={`documents-filters-${currentTab}`}
          filter={filter}
          currentTab={currentTab}
          categories={documentCategories}
          loadingCategories={isLoading}
        />
      )}
      <DriveTour
        stepsMatrix={filteredSteps}
        open={openTour}
        setOpen={setOpenTour}
      />
    </Page>
  )
}

export default DocumentsMain
